import { create } from 'zustand';
import type { AlertType } from '@asi/shared';

// ─── Sound slot types ─────────────────────────────────────────

/**
 * Built-in synthesized sound IDs.
 * These are generated in real-time via Web Audio API — no external files needed.
 */
export type BuiltinSoundId =
  | 'default'   // type-specific default tone
  | 'beep'      // short sharp beep
  | 'chime'     // gentle descending chime
  | 'klaxon'    // urgent fast pulsed tone
  | 'pulse'     // slow pulsed low tone
  | 'siren'     // police-style sweeping siren
  | 'whoop'     // emergency whoop (sweep down→up)
  | 'alert'     // fast 4-beep alert
  | 'buzzer'    // harsh sawtooth buzz
  | 'ding_dong' // doorbell two-tone
  | 'urgent'    // rising 3-tone urgent
  | 'radar'     // single radar ping
  | 'tritone'   // 3 ascending tones (NBC-style)
  | 'bell'      // bright bell with harmonics
  | 'fire_bell' // fast repeated bell strikes
  | 'voice_placeholder'  // reserved for future TTS
  | 'silent';   // no sound

export const BUILTIN_SOUND_LABELS: Record<BuiltinSoundId, string> = {
  default: 'Predeterminado',
  beep: 'Beep corto',
  chime: 'Campana suave',
  klaxon: 'Klaxon urgente',
  pulse: 'Pulso lento',
  siren: 'Sirena policial',
  whoop: 'Whoop de emergencia',
  alert: 'Alerta rápida (4 beeps)',
  buzzer: 'Zumbador',
  ding_dong: 'Ding-dong (timbre)',
  urgent: 'Urgente ascendente',
  radar: 'Ping de radar',
  tritone: 'Tritono ascendente',
  bell: 'Campana brillante',
  fire_bell: 'Campana de incendio',
  voice_placeholder: 'Voz (próximamente)',
  silent: 'Silencio',
};

/** Custom audio uploaded by the user (stored as base64 data URL) */
export interface CustomAudio {
  name: string;
  /** Base64 data URL — e.g., "data:audio/mp3;base64,..." */
  dataUrl: string;
}

/** A single configurable sound slot */
export interface SoundSlot {
  enabled: boolean;
  /** Which built-in sound to use when no custom audio is set */
  builtin: BuiltinSoundId;
  /** If set, play this custom audio instead of builtin */
  custom?: CustomAudio;
}

export interface SoundSettings {
  /** Master enable/disable for all system sounds */
  masterEnabled: boolean;
  /** Volume level: 0.0 – 1.0 */
  volume: number;
  critical: SoundSlot;
  alarm: SoundSlot;
  warning: SoundSlot;
  processConfirm: SoundSlot;
  /**
   * Per-alert-type overrides for SOS-style alerts (mobile / panic button).
   * If a type is not present the dashboard falls back to the `critical` slot.
   * This lets the operator distinguish (for example) FIRE from MEDICAL by ear
   * when multiple alerts arrive in quick succession.
   */
  byType: Partial<Record<AlertType, SoundSlot>>;
}

// ─── Quick reply types ────────────────────────────────────────

export type QuickReplyModal = 
  | 'process'    // Procesar evento ARC individual
  | 'batch'      // Procesamiento ARC en lote
  | 'both'       // Ambos contextos ARC (process + batch)
  | 'sos'        // Chat emergencia SOS
  | 'call'       // Chat llamada cliente
  | 'emergency'  // Todos los chats de emergencia (SOS + CALL)
  | 'all';       // Todos los contextos

export type QuickReplyColor = 'amber' | 'blue' | 'green' | 'red' | 'purple' | 'indigo' | 'pink' | 'slate';

/** Maps a color key to the full Tailwind class string for a chip button */
export const CHIP_COLORS: Record<QuickReplyColor, string> = {
  amber:  'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20',
  blue:   'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400 hover:bg-blue-500/20',
  green:  'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400 hover:bg-green-500/20',
  red:    'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400 hover:bg-red-500/20',
  purple: 'border-purple-500/30 bg-purple-500/10 text-purple-700 dark:text-purple-400 hover:bg-purple-500/20',
  indigo: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-500/20',
  pink:   'border-pink-500/30 bg-pink-500/10 text-pink-700 dark:text-pink-400 hover:bg-pink-500/20',
  slate:  'border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-400 hover:bg-slate-500/20',
};

/** Swatch background classes used in the color picker UI */
export const CHIP_SWATCH_CLS: Record<QuickReplyColor, string> = {
  amber:  'bg-amber-500',
  blue:   'bg-blue-500',
  green:  'bg-green-500',
  red:    'bg-red-500',
  purple: 'bg-purple-500',
  indigo: 'bg-indigo-500',
  pink:   'bg-pink-500',
  slate:  'bg-slate-400',
};

/** Returns the chip class string for the given color key (defaults to amber) */
export function chipCls(color?: string): string {
  return CHIP_COLORS[(color as QuickReplyColor) ?? 'amber'] ?? CHIP_COLORS.amber;
}

export interface QuickReply {
  id: string;
  /** Short display text for the badge chip */
  label: string;
  /** Full text inserted into the textarea when clicked */
  text: string;
  /** Which modal(s) this badge appears in */
  modals: QuickReplyModal;
  /** Chip color key (defaults to 'amber') */
  color?: QuickReplyColor;
  /** Display sort order */
  order: number;
}

// ─── NTP settings ────────────────────────────────────────

export interface NtpSettings {
  /** Whether the NTP sync feature is active */
  enabled: boolean;
  /**
   * HTTP endpoint that returns JSON with a `datetime` or `utc_datetime` ISO-8601 field.
   * Compatible with worldtimeapi.org and any custom endpoint.
   */
  endpointUrl: string;
  /** How often to re-sync in minutes (0 = only on mount, never again) */
  syncIntervalMin: number;
}

// ─── Store ────────────────────────────────────────────────────

interface SettingsState {
  sounds: SoundSettings;
  setSounds: (partial: Partial<SoundSettings>) => void;
  setSlot: (key: keyof Omit<SoundSettings, 'masterEnabled' | 'volume' | 'byType'>, partial: Partial<SoundSlot>) => void;
  setCustomAudio: (key: keyof Omit<SoundSettings, 'masterEnabled' | 'volume' | 'byType'>, audio: CustomAudio | undefined) => void;
  /** Enable a per-alert-type override (creates the slot from the critical defaults if missing). */
  setTypeSlot: (type: AlertType, partial: Partial<SoundSlot>) => void;
  /** Set/clear a custom audio file for a per-alert-type slot. */
  setTypeCustomAudio: (type: AlertType, audio: CustomAudio | undefined) => void;
  /** Remove the per-alert-type override entirely (falls back to critical). */
  clearTypeSlot: (type: AlertType) => void;
  quickReplies: QuickReply[];
  addQuickReply: (data: Omit<QuickReply, 'id' | 'order'>) => void;
  updateQuickReply: (id: string, partial: Partial<Omit<QuickReply, 'id'>>) => void;
  deleteQuickReply: (id: string) => void;
  ntp: NtpSettings;
  setNtp: (partial: Partial<NtpSettings>) => void;
}

const STORAGE_KEY = 'asi-settings-v2';
const NTP_STORAGE_KEY = 'asi-ntp-settings-v1';

const NTP_DEFAULTS: NtpSettings = {
  enabled: false,
  // Default: own Supabase Edge Function — no auth required, returns { ts: <unix ms> }.
  endpointUrl: `${import.meta.env?.VITE_SUPABASE_URL ?? 'https://plgokzikimllnsfczzsu.supabase.co'}/functions/v1/server-time`,
  syncIntervalMin: 15,
};

function loadNtpSettings(): NtpSettings {
  try {
    const raw = localStorage.getItem(NTP_STORAGE_KEY);
    if (raw) return { ...NTP_DEFAULTS, ...(JSON.parse(raw) as Partial<NtpSettings>) };
  } catch { /* ignore */ }
  return { ...NTP_DEFAULTS };
}

function persistNtp(s: NtpSettings) {
  try { localStorage.setItem(NTP_STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

function slotDefaults(): SoundSlot {
  return { enabled: true, builtin: 'default' };
}

function defaults(): SoundSettings {
  return {
    masterEnabled: true,
    volume: 0.7,
    critical:       { ...slotDefaults(), builtin: 'klaxon' },
    alarm:          { ...slotDefaults(), builtin: 'default' },
    warning:        { ...slotDefaults(), enabled: false, builtin: 'beep' },
    processConfirm: { ...slotDefaults(), builtin: 'chime' },
    byType: {},
  };
}

function loadFromStorage(): SoundSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SoundSettings>;
      const def = defaults();
      return {
        masterEnabled: parsed.masterEnabled ?? def.masterEnabled,
        volume: parsed.volume ?? def.volume,
        critical:       { ...def.critical,       ...(parsed.critical       ?? {}) },
        alarm:          { ...def.alarm,           ...(parsed.alarm          ?? {}) },
        warning:        { ...def.warning,         ...(parsed.warning        ?? {}) },
        processConfirm: { ...def.processConfirm,  ...(parsed.processConfirm ?? {}) },
        byType:         { ...(parsed.byType ?? {}) },
      };
    }
  } catch {
    // ignore parse errors
  }
  return defaults();
}

function persist(sounds: SoundSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sounds));
  } catch {
    // quota exceeded — custom audio files are large
  }
}

// ─── Quick replies storage ────────────────────────────────────

const QR_STORAGE_KEY = 'asi-quick-replies-v1';

const DEFAULT_QUICK_REPLIES: QuickReply[] = [
  { id: 'qr-1', label: 'Prueba del sistema',     text: 'Prueba periódica verificada. Sistema en correcto funcionamiento.',  modals: 'both', color: 'amber',  order: 0 },
  { id: 'qr-2', label: 'Verificado con cliente', text: 'Verificado telefónicamente con cliente. Código correcto.',          modals: 'both', color: 'blue',   order: 1 },
  { id: 'qr-3', label: 'Sin incidente',           text: 'Evento sin incidente real. Zona verificada.',                       modals: 'both', color: 'green',  order: 2 },
  { id: 'qr-4', label: 'Despacho no requerido',  text: 'Evento verificado. No se requiere despacho de unidad.',              modals: 'both', color: 'slate',  order: 3 },
];

function loadQuickReplies(): QuickReply[] {
  try {
    const raw = localStorage.getItem(QR_STORAGE_KEY);
    if (raw) return JSON.parse(raw) as QuickReply[];
  } catch {
    // ignore parse errors
  }
  return DEFAULT_QUICK_REPLIES;
}

function persistQuickReplies(replies: QuickReply[]) {
  try {
    localStorage.setItem(QR_STORAGE_KEY, JSON.stringify(replies));
  } catch {
    // ignore
  }
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  sounds: loadFromStorage(),
  quickReplies: loadQuickReplies(),
  ntp: loadNtpSettings(),

  setSounds: (partial) => {
    const next = { ...get().sounds, ...partial };
    persist(next);
    set({ sounds: next });
  },

  setSlot: (key, partial) => {
    const next = {
      ...get().sounds,
      [key]: { ...get().sounds[key], ...partial },
    };
    persist(next);
    set({ sounds: next });
  },

  setCustomAudio: (key, audio) => {
    const next = {
      ...get().sounds,
      [key]: { ...get().sounds[key], custom: audio },
    };
    persist(next);
    set({ sounds: next });
  },

  setTypeSlot: (type, partial) => {
    const current = get().sounds;
    const existing = current.byType[type] ?? { ...current.critical };
    const next: SoundSettings = {
      ...current,
      byType: { ...current.byType, [type]: { ...existing, ...partial } },
    };
    persist(next);
    set({ sounds: next });
  },

  setTypeCustomAudio: (type, audio) => {
    const current = get().sounds;
    const existing = current.byType[type] ?? { ...current.critical };
    const next: SoundSettings = {
      ...current,
      byType: { ...current.byType, [type]: { ...existing, custom: audio } },
    };
    persist(next);
    set({ sounds: next });
  },

  clearTypeSlot: (type) => {
    const current = get().sounds;
    const nextByType = { ...current.byType };
    delete nextByType[type];
    const next: SoundSettings = { ...current, byType: nextByType };
    persist(next);
    set({ sounds: next });
  },

  addQuickReply: (data) => {
    const replies = get().quickReplies;
    const maxOrder = replies.length > 0 ? Math.max(...replies.map((r) => r.order)) : -1;
    const newReply: QuickReply = { id: crypto.randomUUID(), order: maxOrder + 1, ...data };
    const next = [...replies, newReply];
    persistQuickReplies(next);
    set({ quickReplies: next });
  },

  updateQuickReply: (id, partial) => {
    const next = get().quickReplies.map((r) => (r.id === id ? { ...r, ...partial } : r));
    persistQuickReplies(next);
    set({ quickReplies: next });
  },

  deleteQuickReply: (id) => {
    const next = get().quickReplies.filter((r) => r.id !== id);
    persistQuickReplies(next);
    set({ quickReplies: next });
  },

  setNtp: (partial) => {
    const next = { ...get().ntp, ...partial };
    persistNtp(next);
    set({ ntp: next });
  },
}));
